package components.finishingUp

import components.form.commentSection
import components.gridAssessment.entryPreview
import components.project.EditAssemblyBasementRestarbeiten
import components.project.configuration.ConfigurationPreviewWithComments
import components.project.editProcessStateEntry
import components.project.isProjectEditable
import components.project.processStatesList
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.lizergy.model.project.process.state.FinalAccountProcessStateEntry.FinalAccountProcessStates
import it.neckar.lizergy.model.project.process.state.FinishingUpProcessStateEntry.FinishingUpProcessStates
import it.neckar.lizergy.model.project.process.state.current
import it.neckar.lizergy.model.project.process.state.toProcessStateEntry
import it.neckar.lizergy.model.state.SendProcessStatesTuple
import it.neckar.open.formatting.dateFormat
import it.neckar.open.time.nowMillis
import it.neckar.react.common.*
import plannerI18nConfiguration
import react.*
import react.dom.*
import router.useDocumentTitle
import router.useLoadResolvedProjectFromUrl
import services.UiActions
import store.hooks.useLoadProcessStates
import store.hooks.useLoadProcessStatesForComponent
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser

val FinishingUpOverviewFromUrl: FC<Props> = fc("FinishingUpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Fertigmeldung - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    FinishingUpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val FinishingUpOverview: FC<FinishingUpOverviewProps> = fc("FinishingUpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val project = props::project.safeGet()
  val finishingUp = project.finishingUp
  val finalAccount = project.finalAccount

  val finishingUpProcessState = useLoadProcessStatesForComponent(finishingUp.uuid)?.current()?.processState
  val finalAccountProcessState = useLoadProcessStatesForComponent(finalAccount.uuid)?.current()?.processState


  useMemo(finishingUpProcessState) {
    val automaticProcessStates = buildList {
      if (finishingUpProcessState == FinishingUpProcessStates.Done) {

        if (finalAccountProcessState == null) {
          add(SendProcessStatesTuple(finalAccount.uuid, FinalAccountProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(processStateFor = finishingUp, availableProcessStates = FinishingUpProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(finishingUp)

      if (
        finishingUpProcessState == FinishingUpProcessStates.Done &&
        (finalAccountProcessState == null || finalAccountProcessState == FinalAccountProcessStates.Missing)
      ) {
        editProcessStateEntry(processStateFor = finalAccount, availableProcessStates = listOf(FinalAccountProcessStates.Missing), editableStatus = project.isProjectEditable())
        processStatesList(finalAccount)
      }
    }
    div("col-md-6") {
      h5 { +"Bemerkungen zur Fertigmeldung" }
      commentSection(finishingUp)
      h5("mt-3") { +"Bemerkungen zur Abschlussrechnung" }
      commentSection(finalAccount)
    }
  }

  div("row my-3") {
    div("col-lg-6") {
      entryPreview("Inbetriebnahme am", project.startupOperations.date?.let { dateFormat.format(it, plannerI18nConfiguration) })
    }
  }

  EditFertigmeldungChecklist {
    attrs {
      this.project = project
    }
  }

  EditAssemblyBasementRestarbeiten {
    attrs {
      this.project = project
      this.showOnlyRelevant = true
    }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    val currentQuoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)

    MessdatenOverview {
      attrs {
        this.project = project
        this.currentConfiguration = currentQuoteConfiguration
      }
    }

    hr("my-3") {}

    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = currentQuoteConfiguration
      }
    }
  }
}

external interface FinishingUpOverviewProps : Props {
  var project: ResolvedProject
}
